// src/pages/InvitationPage.jsx

import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import * as api from '../services/api';
import LoadingSpinner from '../components/NodeManagement/components/LoadingSpinner';
import ErrorMessage from '../components/NodeManagement/components/ErrorMessage';
import SuccessMessage from '../components/NodeManagement/components/SuccessMessage';
import '../styles/invitation-page.css';

const InvitationPage = () => {
  const { wallet, openWalletModal } = useWallet();
  const [invitationCode, setInvitationCode] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [referralStatus, setReferralStatus] = useState({
    isReferred: false,
    referrerCode: '',
    referrerWallet: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingInvitation, setProcessingInvitation] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Check for invitation code in URL params when component mounts
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const codeFromUrl = queryParams.get('code');
    
    if (codeFromUrl) {
      setInvitationCode(codeFromUrl);
      
      // Update the URL without the parameter to avoid confusion on refresh
      // but only if the browser supports history API
      if (window.history && window.history.replaceState) {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  }, []);

  // Check if user is already referred when wallet connected
  useEffect(() => {
    if (wallet.connected) {
      checkReferralStatus();
    }
  }, [wallet.connected]);

  // Clean up modal classes when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const checkReferralStatus = async () => {
    if (!wallet.connected) return;
    
    try {
      setLoadingStatus(true);
      setError('');
      
      const response = await api.checkReferralStatus(wallet.address);
      
      if (response && response.success) {
        setReferralStatus({
          isReferred: response.data.is_referred,
          referrerCode: response.data.referrer_code || '',
          referrerWallet: response.data.referrer_wallet || ''
        });
        
        if (response.data.is_referred && invitationCode) {
          setError('Your wallet is already bound to a referrer. You cannot use another invitation code.');
        } else if (response.data.is_referred) {
          setSuccess('Your wallet is already bound to a referrer.');
        } else if (invitationCode) {
          setShowConfirmation(true);
        }
      }
    } catch (err) {
      console.error('Failed to check referral status:', err);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleConnectWallet = () => {
    openWalletModal();
  };

  const handleApplyInvitationCode = async () => {
    if (!wallet.connected) {
      openWalletModal();
      return;
    }
    
    if (!invitationCode.trim()) {
      setError('Please enter a valid invitation code');
      return;
    }
    
    try {
      setProcessingInvitation(true);
      setError('');
      setSuccess('');
      
      // Use the correct blockchain network ID
      const networkId = 1; // Replace with dynamic value if needed
      
      // Get signature message
      const messageResponse = await api.generateSignatureMessage(
        wallet.address,
        networkId
      );
      
      if (!messageResponse || !messageResponse.message) {
        throw new Error('Invalid signature message response');
      }
      
      // Sign message
      const signature = await api.signMessage(
        wallet.type,
        wallet.address,
        messageResponse.message
      );
      
      // Apply invitation code
      const response = await api.applyInvitationCode(
        invitationCode,
        wallet.address,
        networkId,
        signature,
        messageResponse.message,
        wallet.type
      );
      
      if (response && response.success) {
        setSuccess('Successfully applied invitation code! You are now referred by this team leader.');
        setReferralStatus({
          isReferred: true,
          referrerCode: invitationCode,
          referrerWallet: response.data?.referrer_wallet || ''
        });
        setShowConfirmation(false);
      } else {
        throw new Error(response?.message || 'Failed to apply invitation code');
      }
    } catch (err) {
      console.error('Failed to apply invitation code:', err);
      setError(err.message || 'Failed to apply invitation code');
    } finally {
      setProcessingInvitation(false);
    }
  };

  const formatWalletAddress = (address) => {
    if (!address) return '';
    return address.substring(0, 6) + '...' + address.substring(address.length - 4);
  };

  return (
    <div className="invitation-page-container">
      <div className="section-wrapper">
        <div className="invitation-section">
          <div className="invitation-card glass-card">
            <h1 className="section-title">Join the Aledger Network</h1>
            
            {loadingStatus ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Checking your status...</p>
              </div>
            ) : referralStatus.isReferred ? (
              <div className="status-container">
                <div className="success-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2>Already Referred</h2>
                <p>Your wallet is already bound to a referrer with code: <strong>{referralStatus.referrerCode}</strong></p>
                
                <div className="referrer-details glass-card">
                  <div className="detail-row">
                    <span className="detail-label">Referrer Wallet:</span>
                    <span className="detail-value">{formatWalletAddress(referralStatus.referrerWallet)}</span>
                  </div>
                </div>
                
                <div className="action-buttons">
                  <a href="/node" className="btn btn-primary">Explore Nodes</a>
                </div>
              </div>
            ) : showConfirmation ? (
              <div className="confirmation-container">
                <h2>Confirm Invitation</h2>
                
                <div className="invitation-code-display">
                  <span className="label">Invitation Code:</span>
                  <span className="code">{invitationCode}</span>
                </div>
                
                <p className="confirmation-note">
                  By confirming, you'll be joining this team leader's network. This action cannot be undone.
                </p>
                
                {error && <ErrorMessage message={error} />}
                {success && <SuccessMessage message={success} />}
                
                <div className="action-buttons">
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowConfirmation(false)}
                    disabled={processingInvitation}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleApplyInvitationCode}
                    disabled={processingInvitation}
                  >
                    {processingInvitation ? (
                      <>
                        <div className="spinner small"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      'Confirm & Join'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="connect-wallet-container">
                <p className="welcome-text">
                  You've been invited to join the Aledger network! To continue, please connect your wallet.
                </p>
                
                {invitationCode && (
                  <div className="invitation-code-display">
                    <span className="label">Invitation Code:</span>
                    <span className="code">{invitationCode}</span>
                  </div>
                )}
                
                {error && <ErrorMessage message={error} />}
                {success && <SuccessMessage message={success} />}
                
                <div className="manual-input">
                  {!invitationCode && (
                    <>
                      <p>If you received an invitation code, you can enter it here:</p>
                      <div className="input-group">
                        <input
                          type="text"
                          placeholder="Enter invitation code"
                          value={invitationCode}
                          onChange={(e) => setInvitationCode(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>
                
                <button className="btn btn-primary connect-btn" onClick={handleConnectWallet}>
                  {wallet.connected ? 'Wallet Connected' : 'Connect Wallet'}
                </button>
                
                {wallet.connected && (
                  <div className="wallet-info">
                    <p>Connected: {formatWalletAddress(wallet.address)}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="benefits-section">
          <h2 className="section-title">Benefits of Joining</h2>
          <div className="benefits-grid">
            <div className="benefit-card glass-card">
              <div className="benefit-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Community Access</h3>
              <p>Join a vibrant community of node operators and investors in the Aledger ecosystem.</p>
            </div>
            
            <div className="benefit-card glass-card">
              <div className="benefit-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1v22M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Multiple Revenue Streams</h3>
              <p>Earn passive income through node rewards and referral commissions from your downline network.</p>
            </div>
            
            <div className="benefit-card glass-card">
              <div className="benefit-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23 6l-9.5 9.5-5-5L1 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 6h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Team Building</h3>
              <p>Build your own network by inviting others and grow your earnings as your team expands.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationPage;
